import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import { marked } from 'marked';
import { detectRendererKind, monacoLanguageForPath } from '../renderers/registry.js';

let _workerLoader = null;
export function configureMonacoWorker(loader) {
  _workerLoader = loader;
}

if (typeof globalThis !== 'undefined') {
  globalThis.MonacoEnvironment = globalThis.MonacoEnvironment || {
    getWorker() {
      if (_workerLoader) return _workerLoader();
      throw new Error('Monaco worker not configured');
    },
  };
}

function escapeHtml(str = '') {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export class FilePane {
  constructor({ container, readonly = false, onSave, onDirty, onError }) {
    this.container = container;
    this.readonly = readonly;
    this.onSave = onSave;
    this.onDirty = onDirty;
    this.onError = onError;
    this.currentPath = null;
    this.currentPayload = null;
    this.editor = null;
    this.dirty = false;
    this._renderShell();
  }

  _renderShell() {
    this.container.innerHTML = `
      <div class="repo-pane">
        <div class="repo-pane-toolbar">
          <div class="repo-pane-title" data-pane-title>Select a file</div>
          <div class="repo-pane-actions">
            <button type="button" class="repo-pane-btn" data-pane-save disabled>Save</button>
            <button type="button" class="repo-pane-btn" data-pane-revert disabled>Revert</button>
            <button type="button" class="repo-pane-btn" data-pane-download disabled>Download</button>
          </div>
        </div>
        <div class="repo-pane-body" data-pane-body>
          <div class="repo-pane-empty">Select a file from the tree to view or edit it.</div>
        </div>
      </div>
    `;
    this.titleEl = this.container.querySelector('[data-pane-title]');
    this.bodyEl = this.container.querySelector('[data-pane-body]');
    this.saveBtn = this.container.querySelector('[data-pane-save]');
    this.revertBtn = this.container.querySelector('[data-pane-revert]');
    this.downloadBtn = this.container.querySelector('[data-pane-download]');

    this.saveBtn?.addEventListener('click', () => this.save().catch((err) => this.onError?.(err)));
    this.revertBtn?.addEventListener('click', () => this.revert());
    this.downloadBtn?.addEventListener('click', () => this.download());
  }

  disposeEditor() {
    if (this.editor) {
      this.editor.dispose();
      this.editor = null;
    }
  }

  setReadonly(readonly) {
    this.readonly = readonly;
    if (this.editor) {
      this.editor.updateOptions({ readOnly: readonly });
    }
    this._updateButtons();
  }

  async openFile(path, payload) {
    this.disposeEditor();
    this.currentPath = path;
    this.currentPayload = payload;
    this.dirty = false;
    this.onDirty?.(path, false);
    this.titleEl.textContent = path;
    this._updateButtons();

    const kind = detectRendererKind(path, payload.mime);
    this.bodyEl.innerHTML = '';

    if (kind === 'image') {
      const src = payload.encoding === 'base64'
        ? `data:${payload.mime || 'image/png'};base64,${payload.content}`
        : payload.content;
      this.bodyEl.innerHTML = `<div class="repo-pane-image"><img src="${src}" alt="${escapeHtml(path)}" /></div>`;
      return;
    }

    if (kind === 'pdf') {
      const src = payload.encoding === 'base64'
        ? `data:application/pdf;base64,${payload.content}`
        : payload.content;
      this.bodyEl.innerHTML = `<iframe class="repo-pane-iframe" src="${src}" title="${escapeHtml(path)}"></iframe>`;
      return;
    }

    if (kind === 'binary') {
      this.bodyEl.innerHTML = `
        <div class="repo-pane-binary">
          <p>Binary file (${payload.mime || 'unknown'}) — ${payload.size || 0} bytes</p>
          <p>Use Download to save a local copy.</p>
        </div>
      `;
      return;
    }

    if (kind === 'csv') {
      const text = payload.encoding === 'base64'
        ? atob(payload.content)
        : payload.content;
      const rows = text.trim().split(/\r?\n/).slice(0, 200).map((line) => line.split(','));
      const table = rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`).join('');
      this.bodyEl.innerHTML = `<div class="repo-pane-table-wrap"><table class="repo-pane-table"><tbody>${table}</tbody></table></div>`;
      return;
    }

    if (kind === 'markdown' && this.readonly) {
      const text = payload.encoding === 'base64' ? atob(payload.content) : payload.content;
      this.bodyEl.innerHTML = `<div class="repo-pane-markdown">${marked.parse(text)}</div>`;
      return;
    }

    if (kind === 'html' && this.readonly) {
      const text = payload.encoding === 'base64' ? atob(payload.content) : payload.content;
      this.bodyEl.innerHTML = `<iframe class="repo-pane-iframe" sandbox="allow-scripts" srcdoc="${escapeHtml(text)}"></iframe>`;
      return;
    }

    const text = payload.encoding === 'base64' ? atob(payload.content) : payload.content;
    const host = document.createElement('div');
    host.className = 'repo-pane-editor-host';
    host.setAttribute('data-pane-editor-host', '');
    this.bodyEl.appendChild(host);

    this.editor = monaco.editor.create(host, {
      value: text,
      language: monacoLanguageForPath(path),
      theme: 'vs',
      automaticLayout: true,
      minimap: { enabled: false },
      readOnly: this.readonly,
      scrollBeyondLastLine: false,
      fontSize: 13,
      lineNumbers: 'on',
    });

    this.editor.onDidChangeModelContent(() => {
      if (!this.readonly) {
        this.dirty = true;
        this.onDirty?.(this.currentPath, true);
        this._updateButtons();
      }
    });
  }

  getCurrentContent() {
    if (this.editor) return this.editor.getValue();
    return this.currentPayload?.encoding === 'base64'
      ? this.currentPayload.content
      : (this.currentPayload?.content || '');
  }

  getCurrentEncoding() {
    if (this.editor) return 'utf-8';
    return this.currentPayload?.encoding || 'utf-8';
  }

  async save() {
    if (!this.currentPath || this.readonly || !this.onSave) return;
    const result = await this.onSave(this.currentPath, this.getCurrentContent(), this.getCurrentEncoding());
    this.dirty = false;
    this.onDirty?.(this.currentPath, false);
    this._updateButtons();
    return result;
  }

  revert() {
    if (!this.currentPath || !this.currentPayload) return;
    this.openFile(this.currentPath, this.currentPayload);
  }

  download() {
    if (!this.currentPath || !this.currentPayload) return;
    const blob = this.currentPayload.encoding === 'base64'
      ? new Blob([Uint8Array.from(atob(this.getCurrentContent()), (c) => c.charCodeAt(0))], { type: this.currentPayload.mime || 'application/octet-stream' })
      : new Blob([this.getCurrentContent()], { type: this.currentPayload.mime || 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.currentPath.split('/').pop() || 'download';
    a.click();
    URL.revokeObjectURL(url);
  }

  _updateButtons() {
    const hasFile = Boolean(this.currentPath);
    const editable = hasFile && !this.readonly && Boolean(this.editor);
    if (this.saveBtn) this.saveBtn.disabled = !editable || !this.dirty;
    if (this.revertBtn) this.revertBtn.disabled = !editable || !this.dirty;
    if (this.downloadBtn) this.downloadBtn.disabled = !hasFile;
  }
}
